/**
 * How a payment method's commission is expressed.
 *
 * - `percentage` — `commission` is a rate (0–100) applied to the gross refund.
 * - `fixed`      — `commission` is a flat AED amount retained regardless of refund size.
 *
 * Mirrors the `commission_type` enum on `payment_methods` and `credit_notes`.
 */
export type CommissionType = 'percentage' | 'fixed';

/** Default applied when the API omits `commission_type` (pre-migration rows). */
export const DEFAULT_COMMISSION_TYPE: CommissionType = 'percentage';

/**
 * A `payment_methods` row as returned by `GET /api/v1/payment-methods`.
 *
 * Named `AdminPaymentMethod` rather than `PaymentMethod` because the storefront
 * already exports a different `PaymentMethod` shape from
 * `models/product/payment-method.model.ts`; two same-named exports would collide
 * in the `@sgm-frontend/core` barrel and break the whole lib.
 */
export interface AdminPaymentMethod {
  id: number | string;
  /** Stable machine key: `card` | `cash` | `tabby` | `tamara`. */
  code: string;
  name: string;
  is_active: boolean;
  /**
   * Commission value. Interpreted per {@link commission_type}: a 0–100 rate when
   * `percentage`, a flat AED amount when `fixed`. DECIMAL(10,2) → may arrive as a string.
   */
  commission: number | string;
  /** Absent on rows created before the commission-type migration; treat as `percentage`. */
  commission_type?: CommissionType | null;
  created_at?: string;
  updated_at?: string;
}

/** Request body for `PUT /api/v1/payment-methods/:id`. */
export interface UpdatePaymentMethodPayload {
  commission: number;
  commission_type: CommissionType;
}

/** Normalises a possibly-missing/legacy `commission_type` into a usable value. */
export function toCommissionType(value: unknown): CommissionType {
  return value === 'fixed' ? 'fixed' : DEFAULT_COMMISSION_TYPE;
}

/**
 * Commission retained on a refund, in AED.
 * Percentage commissions scale with the base they are given; fixed ones do not.
 * The base is the NET TOTAL (products + shipping + handling − return fee), not the
 * product lines alone — mirror of the backend's `_computeCreditNoteAmounts`.
 */
export function calculateCommissionAmount(
  baseAmount: number,
  commission: number,
  type: CommissionType
): number {
  const value = Number(commission) || 0;
  if (value <= 0) return 0;
  return type === 'fixed' ? value : (Math.max(baseAmount, 0) * value) / 100;
}
